import { infiniteQueryOptions, useMutation, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { includeKeysListAgreementLinks, includeKeysListPdfs } from '@/_pages/invoices/shared';
import type { AccountReceivablePaymentSchema } from '@/_schemas/account-receivables/account-receivable-payment-schema';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  AccountReceivableAllArDetailsQuerySchema,
  AccountReceivableClientAggregatesQuerySchema,
  AccountsReceivablesAllQuerySchema,
  AccountsReceivablesQuerySchema,
} from './schemas';

type AccountReceivablesQueryResponse = AccountsReceivablesQuerySchema & {
  agreement_id: number;
  agreement_installment_id: number | null;
  payment_date: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  agreement: unknown;
  agreement_installment: unknown;
};

const accountReceivablesInvoiceActionIncludes = [
  ...includeKeysListPdfs.map((key) => `invoice.${key}`),
  'invoice.latest_email_history',
  ...includeKeysListAgreementLinks.map((key) => `invoice.${key}`),
];

const accountReceivablesIncludeKeysBase = [
  'invoice.client.logo',
  'invoice.brand.icon',
  ...accountReceivablesInvoiceActionIncludes,
];
const accountReceivablesIncludeKeysStr = accountReceivablesIncludeKeysBase.join(',');

/** Advance-filter: balance due strictly greater than zero (money still owed). Used when the list is limited to open balances. */
export const ACCOUNT_RECEIVABLE_LIST_BALANCE_DUE_DEFAULT_CLAUSE = 'above[balance_due]=0';

const DEFAULT_ACCOUNT_RECEIVABLES_LIST_PARAMS = `${defaultApiParamStr}&filter[advance_filtering]=${ACCOUNT_RECEIVABLE_LIST_BALANCE_DUE_DEFAULT_CLAUSE}`;

export function getAccountReceivablesQuery(apiParamStr = DEFAULT_ACCOUNT_RECEIVABLES_LIST_PARAMS) {
  return {
    queryKey: ['ACCOUNT_RECEIVABLES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AccountsReceivablesAllQuerySchema>({
        endpoint: `/api/v1/account-receivables?include=${accountReceivablesIncludeKeysStr}`,
        params: `${apiParamStr}`,
      });

      return res;
    },
  };
}

// export function useGetAccountReceivables(apiParamStr: string) {
//   return useSuspenseQuery(getAccountReceivablesQuery(apiParamStr));
// }

/** Nested expanded company rows still load flat AR lines with scroll-to-load; not used for the main company list. */
export function getAccountReceivablesInfiniteQueryOptions(apiParamStr = '?per_page=50') {
  return infiniteQueryOptions<AccountsReceivablesAllQuerySchema>({
    queryKey: ['ACCOUNT_RECEIVABLES', apiParamStr],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get<AccountsReceivablesAllQuerySchema>({
        endpoint: `/api/v1/account-receivables?include=${accountReceivablesIncludeKeysStr}`,
        params: `${apiParamStr}&page=${pageParam}`,
      });
      return res;
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
  });
}

// export function useGetAccountReceivablesInfinite(apiParamStr: string) {
//   return useSuspenseInfiniteQuery(getAccountReceivablesInfiniteQueryOptions(apiParamStr));
// }

export function getAccountReceivableClientAggregatesQuery(apiParamStr = '?per_page=500') {
  return {
    queryKey: ['ACCOUNT_RECEIVABLE_CLIENT_AGGREGATES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AccountReceivableClientAggregatesQuerySchema>({
        endpoint: `/api/v1/account-receivables/by-clients`,
        params: `${apiParamStr}`,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function useGetAccountReceivableClientAggregates(apiParamStr: string) {
  return useSuspenseQuery(getAccountReceivableClientAggregatesQuery(apiParamStr));
}

/** Export-only: `fetch_all=true` returns all filtered rows in one response (omit pagination params). */
function withFetchAllParam(apiParamStr: string) {
  const [prefix, query = ''] = apiParamStr.split('?');
  const params = query
    .split('&')
    .filter(
      (part) =>
        part.length > 0 && !part.startsWith('page=') && !part.startsWith('per_page=') && !part.startsWith('fetch_all=')
    );
  params.push('fetch_all=true');
  return `${prefix}?${params.join('&')}`;
}

export function getAccountReceivableClientAggregatesExportQuery(apiParamStr: string) {
  const exportParamStr = withFetchAllParam(apiParamStr);
  return {
    queryKey: ['ACCOUNT_RECEIVABLE_CLIENT_AGGREGATES_EXPORT', exportParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AccountReceivableClientAggregatesQuerySchema>({
        endpoint: `/api/v1/account-receivables/by-clients`,
        params: exportParamStr,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

function stripRawExportQueryParams(apiParamStr: string) {
  const [prefix, query = ''] = apiParamStr.split('?');
  const params = query
    .split('&')
    .filter(
      (part) =>
        part.length > 0 &&
        !part.startsWith('page=') &&
        !part.startsWith('per_page=') &&
        !part.startsWith('sort=') &&
        !part.startsWith('fetch_all=')
    );
  return params.length > 0 ? `${prefix}?${params.join('&')}` : prefix;
}

export function getAccountReceivableAllArDetailsExportQuery(apiParamStr: string) {
  const exportParamStr = stripRawExportQueryParams(apiParamStr);
  return {
    queryKey: ['ACCOUNT_RECEIVABLE_ALL_AR_DETAILS_EXPORT', exportParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AccountReceivableAllArDetailsQuerySchema>({
        endpoint: '/api/v1/account-receivables/all-ar-details',
        params: exportParamStr,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function getAccountReceivableClientAggregatesInfiniteQueryOptions(apiParamStr = '?per_page=50') {
  return infiniteQueryOptions<AccountReceivableClientAggregatesQuerySchema>({
    queryKey: ['ACCOUNT_RECEIVABLE_CLIENT_AGGREGATES', apiParamStr],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get<AccountReceivableClientAggregatesQuerySchema>({
        endpoint: `/api/v1/account-receivables/by-clients`,
        params: `${apiParamStr}&page=${pageParam}`,
        preventAuditLog: true,
      });
      return res;
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
  });
}

export function useGetAccountReceivableClientAggregatesInfinite(apiParamStr: string) {
  return useSuspenseInfiniteQuery(getAccountReceivableClientAggregatesInfiniteQueryOptions(apiParamStr));
}

export function getAccountReceivableTotalQuery() {
  return {
    queryKey: ['ACCOUNT_RECEIVABLE_TOTAL'],
    queryFn: async () => {
      const res = await apiClient.get<{ overall_totals: { total_balance_due: number } }>({
        endpoint: `/api/v1/account-receivables/by-clients`,
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function useGetAccountReceivableTotal() {
  return useSuspenseQuery(getAccountReceivableTotalQuery());
}

export const invalidateAccountReceivablesQuery = () => {
  queryClient.invalidateQueries({ queryKey: ['ACCOUNT_RECEIVABLES'] });
  queryClient.invalidateQueries({ queryKey: ['ACCOUNT_RECEIVABLE_CLIENT_AGGREGATES'] });
};

export const removeAccountReceivablesQuery = () => {
  queryClient.removeQueries({ queryKey: ['ACCOUNT_RECEIVABLES'] });
  queryClient.removeQueries({ queryKey: ['ACCOUNT_RECEIVABLE_CLIENT_AGGREGATES'] });
};

const accountReceivableStr = [
  'invoice.brand.icon',
  'invoice.client.logo',
  'account_receivable_payments.ar_payment_collection.payment_transactions.agreement_payments',
  'account_receivable_payments.payment_transaction.payment_method',
];

export function useGetAccountReceivable(id: string) {
  return useSuspenseQuery(getAccountReceivableQuery(id));
}

export function getAccountReceivableQuery(id: string) {
  return {
    queryKey: ['ACCOUNT_RECEIVABLE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: AccountReceivablesQueryResponse }>({
        endpoint: `/api/v1/account-receivables/${id}`,
        params: `?include=${accountReceivableStr.join(',')}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateAccountReceivableQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['ACCOUNT_RECEIVABLE', id] });
};

export const useGetAccountReceivablesMutation: UseControlledMutation<AccountReceivablesQueryResponse> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['ACCOUNT_RECEIVABLES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<AccountsReceivablesAllQuerySchema>({
            endpoint: '/api/v1/account-receivables',
            params: {
              search,
              per_page,
              include: accountReceivablesIncludeKeysStr,
            },
          });

          return orderBy(res?.data ?? [], ['id']) || [];
        },
      });
    },
  });

export const useCreateARPaymentMutation = () =>
  useMutation<{ data: number }, ApiError, AccountReceivablePaymentSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/widgets/ar-payment-widget/submit',
        payload,
      }),
  });
